/**
 * Dropzone de imagen para productos.
 *
 * Espejo del componente desktop
 * (`apps/desktop/src/renderer/src/components/ImageDropzone.tsx`)
 * pero apuntando a `trpc.images.upload` del worker (almacena en R2) en
 * lugar del protocolo local `atreus-asset://` del desktop.
 *
 * La URL que devuelve el worker es relativa (ej: `/images/<tenant>/<uuid>.jpg`),
 * así que la resolvemos contra `WORKER_URL` al renderer para que el
 * browser la cargue correctamente.
 */

import { Button, cn } from '@atreus/ui';
import { ImagePlus, Loader2, Sparkles, Trash2, Upload } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { WORKER_URL } from '../lib/cloud.js';
import { formatBytes, optimizeImage } from '../lib/imageOptimize.js';
import { trpc } from '../lib/trpc.js';

const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;
const ACCEPTED_EXT = ['.jpg', '.jpeg', '.png', '.webp'] as const;
/**
 * Límite del archivo de ENTRADA (antes de optimizar). La optimización
 * deja la mayoría en <200KB; este techo es para evitar cargar archivos
 * absurdos al canvas (fotos RAW, screenshots de 16K, etc.).
 */
const MAX_INPUT_BYTES = 20 * 1024 * 1024;

interface Props {
  value: string | null;
  onChange: (url: string | null) => void;
}

/**
 * Lee un Blob como base64 puro (sin el prefijo `data:...,`) para mandarlo
 * al worker. La optimización se hace antes y produce un Blob WebP/JPEG,
 * no un File — por eso el helper acepta Blob, no File.
 */
const readBlobAsBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('No se pudo leer el archivo'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error('Error leyendo archivo'));
    reader.readAsDataURL(blob);
  });

/**
 * Si el worker devuelve una URL relativa (`/images/...`), la absolutiza
 * contra `WORKER_URL`. Si ya es absoluta (caso hipotético de CDN
 * externa), la respeta tal cual.
 */
export const resolveImageUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${WORKER_URL}${url}`;
};

interface SavingsInfo {
  originalBytes: number;
  optimizedBytes: number;
  width: number;
  height: number;
}

export const ImageDropzone = ({ value, onChange }: Props) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savings, setSavings] = useState<SavingsInfo | null>(null);
  const [optimizing, setOptimizing] = useState(false);
  const upload = trpc.images.upload.useMutation();

  const handleFile = useCallback(
    async (file: File): Promise<void> => {
      setError(null);
      setSavings(null);
      if (
        !ACCEPTED_MIME.includes(
          file.type as (typeof ACCEPTED_MIME)[number],
        )
      ) {
        setError('Formato no soportado. Usa JPG, PNG o WEBP.');
        return;
      }
      if (file.size > MAX_INPUT_BYTES) {
        setError(`La imagen supera ${MAX_INPUT_BYTES / 1024 / 1024}MB.`);
        return;
      }
      try {
        setOptimizing(true);
        const result = await optimizeImage(file);
        setOptimizing(false);
        const base64 = await readBlobAsBase64(result.blob);
        const saved = await upload.mutateAsync({
          base64,
          ext: result.ext,
        });
        setSavings({
          originalBytes: result.originalBytes,
          optimizedBytes: result.optimizedBytes,
          width: result.width,
          height: result.height,
        });
        onChange(saved.url);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Error al subir la imagen');
      } finally {
        setOptimizing(false);
      }
    },
    [onChange, upload],
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = '';
  };

  if (value) {
    return (
      <div>
        <div className="relative overflow-hidden rounded-md border border-border bg-surface-muted">
          <img
            src={resolveImageUrl(value) ?? undefined}
            alt="Previsualización"
            className="h-48 w-full object-cover"
          />
          <div className="flex items-center justify-end gap-2 border-t border-border bg-surface px-3 py-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => fileInput.current?.click()}
            >
              <Upload className="size-3.5" /> Reemplazar
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange(null);
                setSavings(null);
              }}
            >
              <Trash2 className="size-3.5" /> Quitar
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPTED_EXT.join(',')}
              className="hidden"
              onChange={onPick}
            />
          </div>
        </div>
        {savings ? <SavingsHint info={savings} /> : null}
      </div>
    );
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInput.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        className={cn(
          'flex h-48 flex-col items-center justify-center gap-2 rounded-md border border-dashed px-4 text-center transition-colors',
          dragActive
            ? 'border-fg bg-surface-muted'
            : 'border-border bg-surface hover:border-fg-muted hover:bg-surface-muted',
          (optimizing || upload.isPending) && 'pointer-events-none opacity-60',
        )}
      >
        {optimizing ? (
          <>
            <Sparkles className="size-6 animate-pulse text-fg-muted" />
            <div className="text-sm text-fg-muted">Optimizando…</div>
          </>
        ) : upload.isPending ? (
          <>
            <Loader2 className="size-6 animate-spin text-fg-muted" />
            <div className="text-sm text-fg-muted">Subiendo…</div>
          </>
        ) : (
          <>
            <ImagePlus className="size-6 text-fg-muted" />
            <div className="text-sm font-medium">
              Arrastra una imagen o haz click
            </div>
            <div className="text-xs text-fg-subtle">
              JPG, PNG o WEBP · se optimiza automáticamente
            </div>
          </>
        )}
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPTED_EXT.join(',')}
          className="hidden"
          onChange={onPick}
        />
      </div>
      {error ? <div className="mt-2 text-xs text-danger">{error}</div> : null}
    </div>
  );
};

/**
 * Muestra el ahorro del proceso de optimización. Se renderiza solo tras
 * un upload exitoso — feedback opcional que valida que la magia ocurrió.
 */
const SavingsHint = ({ info }: { info: SavingsInfo }) => {
  const delta = info.originalBytes - info.optimizedBytes;
  const pct =
    info.originalBytes > 0
      ? Math.round((delta / info.originalBytes) * 100)
      : 0;
  // Si por alguna razón quedó más pesada (raro, ej: imagen ya chica),
  // omitimos el hint para no confundir al usuario.
  if (delta <= 0) return null;
  return (
    <div className="mt-2 flex items-center gap-1.5 text-xs text-fg-muted">
      <Sparkles className="size-3 text-success" />
      <span>
        Optimizada: {formatBytes(info.originalBytes)} →{' '}
        <strong className="text-fg">
          {formatBytes(info.optimizedBytes)}
        </strong>{' '}
        ({pct}% menos) · {info.width}×{info.height}px
      </span>
    </div>
  );
};
